import React, { useEffect } from "react";
import { ButtonWrap, HamburgerButton, Header as GenericHeader } from "halifax";

import { Route, RouteComponentProps, Switch } from "react-router-dom";
import clsx from "clsx";
import { Badge, Box } from "@material-ui/core";
import { WalletSummaryResponse } from "redmond";
import {
  getUrlWithUtmParams,
  useIsTravelApp,
  useUserContext,
  useUtmParams,
} from "@capone/common";

import CapOneLogo from "../../../assets/b2b/capone-logo.svg";
import {
  PATH_FLIGHTS,
  PATH_FLIGHTS_BOOK_CONFIRMATION,
  PATH_HOTELS,
  PATH_HOTELS_BOOK_CONFIRMATION,
  PATH_CARS,
  PATH_FLIGHTS_SEARCH,
  PATH_HOTELS_SEARCH,
  PATH_CARS_SEARCH,
  PATH_PREMIER_COLLECTION_SEARCH,
  PATH_PREMIER_COLLECTION_BOOK_CONFIRMATION,
  PATH_CARS_BOOK_CONFIRMATION,
  PATH_HOME,
  PATH_TRAVEL_WALLET,
  PATH_PREMIER_VACATION_RENTALS_SEARCH,
  PATH_FLIGHTS_BOOK_WITH_VI_CONFIRMATION,
  PATH_HOTELS_AVAILABILITY,
  PATH_HOTELS_SHOP,
  PATH_CARS_AVAILABILITY,
  PATH_CARS_SHOP,
  PATH_FLIGHTS_SHOP,
  PATH_HOTELS_ANCILLARY,
  PATH_HOTELS_ANCILLARY_CUSTOMIZE,
  PATH_PACKAGE_HOTEL_SHOP,
  PATH_PACKAGE_FLIGHT_SHOP,
  PATH_STAYS,
  PATH_STAYS_SEARCH,
  PATH_STAYS_AVAILABILITY,
  PATH_PACKAGE_REVIEW,
  PATH_PACKAGES_BOOK_CONFIRMATION,
  PATH_PACKAGES_HOTEL_AVAILABILITY,
  PATH_HOTELS_FLIGHTS_HOTEL_AVAILABILITY,
  PATH_HOTELS_FLIGHTS_HOTEL_SHOP,
  PATH_HOTELS_FLIGHTS_FLIGHT_SHOP,
  PATH_HOTELS_FLIGHTS_REVIEW,
  PATH_ACTIVITIES,
  PATH_ACTIVITIES_AVAILABILITY,
  PATH_ACTIVITIES_BOOK_CONFIRMATION,
  PATH_ACTIVITIES_SHOP,
  PATH_FAVORITES,
  PATH_VACATION_RENTALS_BOOK_CONFIRMATION,
  PATH_EXCHANGE_FLIGHT_SHOP,
  PATH_FLIGHTS_SHOP_MULTICITY,
} from "../../../utils/urlPaths";
import { CAP_ONE_LOGO_ALT } from "../../../utils/constants";
import MobileHamburgerMenu from "../../MobileHamburgerMenu";
import "./styles.scss";
import { fetchTravelWalletOffers } from "../../../api/v1/takeovers/fetchTravelWalletOffers";
import {
  AVAILABLE,
  CONTROL,
  CUSTOMER_PROFILE_EXPERIMENT,
  CUSTOMER_PROFILE_VARIANTS,
  EXPERIENCES_EXPERIMENT,
  getExperimentVariant,
  getExperimentVariantCustomVariants,
  GLOBAL_MOBILE_NAV_EXPERIMENT,
  PREMIER_COLLECTION_EXPERIMENT,
  TRAVEL_WALLET_OFFER_EXPERIMENT,
  useExperiments,
} from "../../../context/experiments";
import config from "../../../utils/config";
import fetchUserPreferencesStatus, {
  UserPreferencesStatusPayload,
} from "../../../api/v1/user/fetchUserPreferencesStatus";

export const HIDE_ON_SCROLL_PATHS = [
  PATH_HOTELS_AVAILABILITY,
  PATH_STAYS_AVAILABILITY,
  /\/stays\/availability.+/,
  PATH_FAVORITES,
  PATH_HOTELS_SHOP,
  PATH_HOTELS_ANCILLARY,
  PATH_HOTELS_ANCILLARY_CUSTOMIZE,
  PATH_CARS_AVAILABILITY,
  PATH_CARS_SHOP,
  PATH_FLIGHTS_SHOP,
  new RegExp(`${PATH_FLIGHTS_SHOP}.+`),
  PATH_FLIGHTS_SHOP_MULTICITY,
  PATH_PACKAGES_HOTEL_AVAILABILITY,
  PATH_HOTELS_FLIGHTS_HOTEL_AVAILABILITY,
  PATH_HOTELS_FLIGHTS_HOTEL_SHOP,
  PATH_HOTELS_FLIGHTS_FLIGHT_SHOP,
  PATH_HOTELS_FLIGHTS_REVIEW,
  PATH_PACKAGE_HOTEL_SHOP,
  PATH_PACKAGE_FLIGHT_SHOP,
  PATH_PACKAGE_REVIEW,
  PATH_ACTIVITIES_AVAILABILITY,
  PATH_ACTIVITIES_SHOP,
  new RegExp(`${PATH_EXCHANGE_FLIGHT_SHOP.replace("/", "\\/")}\\/?`),
];

interface IMobileHeaderProps {
  language: string; // eslint-disable-line react/no-unused-prop-types -- required by parent component contract
  className?: string;
}

const MobileHeader = (props: IMobileHeaderProps) => {
  const { className } = props;
  const { isTravelApp } = useIsTravelApp();
  const [showMenu, setShowMenu] = React.useState(false);
  const [openSupportModal, setOpenSupportModal] = React.useState(false);
  const [offerCount, setOfferCount] = React.useState(0);
  const [userPreferencesStatus, setUserPreferencesStatus] =
    React.useState<UserPreferencesStatusPayload>();
  const { sessionInfo, isBusinessEligible } = useUserContext("capone");
  const utmParams = useUtmParams();

  const corporatePortalUrl = getUrlWithUtmParams(
    config.corporatePortalUrl || "",
    utmParams
  );

  const expState = useExperiments();
  const isTravelWalletOfferExperiment =
    getExperimentVariant(
      expState.experiments,
      TRAVEL_WALLET_OFFER_EXPERIMENT
    ) === AVAILABLE;

  const isPremierCollectionEnabled =
    getExperimentVariant(
      expState.experiments,
      PREMIER_COLLECTION_EXPERIMENT
    ) === AVAILABLE;

  const isCustomerProfileExperiment =
    getExperimentVariantCustomVariants(
      expState.experiments,
      CUSTOMER_PROFILE_EXPERIMENT,
      CUSTOMER_PROFILE_VARIANTS
    ) !== CONTROL;

  const isGlobalMobileNavExperiment =
    getExperimentVariant(expState.experiments, GLOBAL_MOBILE_NAV_EXPERIMENT) ===
    AVAILABLE;

  const isExperiencesExperiment =
    getExperimentVariant(expState.experiments, EXPERIENCES_EXPERIMENT) ===
    AVAILABLE;

  useEffect(() => {
    const fetchPreferencesStatus = async () => {
      const prefsStatus = await fetchUserPreferencesStatus();
      setUserPreferencesStatus(prefsStatus);
    };
    if (isCustomerProfileExperiment) {
      fetchPreferencesStatus();
    }
  }, [isCustomerProfileExperiment]);

  useEffect(() => {
    if (sessionInfo.userId.length > 0) {
      fetchTravelWalletOffers().then((response) => {
        const hasCredit = !!(response as WalletSummaryResponse).credit;
        const fetchedOfferCount = (response as WalletSummaryResponse).offerCount || 0;
        const totalCount = hasCredit ? fetchedOfferCount + 1 : fetchedOfferCount;
        setOfferCount(totalCount);
      });
    }
  }, [sessionInfo.userId]);

  const hamburger =
    isTravelWalletOfferExperiment && !isGlobalMobileNavExperiment ? (
      <Badge badgeContent={offerCount} className="offer-count">
        <HamburgerButton
          onOpen={() => setShowMenu(true)}
          onClose={() => setShowMenu(false)}
          open={showMenu}
          menu={
            <MobileHamburgerMenu
              onClose={() => setShowMenu(false)}
              offerCount={offerCount}
              openSupportModal={openSupportModal}
              setOpenSupportModal={setOpenSupportModal}
              isTravelWalletOfferExperiment={isTravelWalletOfferExperiment}
              isPremierCollectionEnabled={isPremierCollectionEnabled}
              isCustomerProfileExperiment={isCustomerProfileExperiment}
              showCustomerProfileNewBadge={
                userPreferencesStatus?.hasUsedPreferences === false
              }
              tenant="capone"
              corporatePortalUrl={isBusinessEligible ? corporatePortalUrl : ""}
              isExperiencesExperiment={isExperiencesExperiment}
            />
          }
        />
      </Badge>
    ) : (
      <HamburgerButton
        onOpen={() => setShowMenu(true)}
        onClose={() => setShowMenu(false)}
        open={showMenu}
        menu={
          <MobileHamburgerMenu
            onClose={() => setShowMenu(false)}
            offerCount={offerCount}
            openSupportModal={openSupportModal}
            setOpenSupportModal={setOpenSupportModal}
            isTravelWalletOfferExperiment={isTravelWalletOfferExperiment}
            isPremierCollectionEnabled={isPremierCollectionEnabled}
            isCustomerProfileExperiment={isCustomerProfileExperiment}
            showCustomerProfileNewBadge={
              userPreferencesStatus?.hasUsedPreferences === false
            }
            tenant="capone"
            corporatePortalUrl={isBusinessEligible ? corporatePortalUrl : ""}
            isExperiencesExperiment={isExperiencesExperiment}
          />
        }
      />
    );

  const renderGenericHeader = (
    isConfirmation: boolean,
    browserRouterProps: RouteComponentProps,
    hideOnScroll: boolean
  ) => (
    <GenericHeader
      dataTestId="mobile-header"
      isMobile
      shadow={className === "homepage" && !isTravelApp}
      className={clsx("b2b", "has-logo", className, {
        confirmation: isConfirmation,
        "hide-travel-app": isTravelApp,
      })}
      left={
        <ButtonWrap onClick={() => {
          const homePath = browserRouterProps.history.location.pathname.startsWith(PATH_ACTIVITIES)
            ? PATH_ACTIVITIES
            : PATH_HOME;
          browserRouterProps.history.push(homePath);
        }}>
          <Box className="logo-container">
            <img src={CapOneLogo} alt={CAP_ONE_LOGO_ALT} />
          </Box>
        </ButtonWrap>
      }
      right={hamburger}
      hideOnScroll={hideOnScroll}
    />
  );

  return (
    <Switch>
      <Route
        path={[
          PATH_FLIGHTS,
          PATH_HOTELS,
          PATH_STAYS,
          PATH_CARS,
          PATH_TRAVEL_WALLET,
        ]}
        render={(browserRouterProps: RouteComponentProps) =>
          renderGenericHeader(false, browserRouterProps, false)
        }
        exact
      />
      <Route
        path={[
          PATH_FLIGHTS_BOOK_CONFIRMATION,
          PATH_FLIGHTS_BOOK_WITH_VI_CONFIRMATION,
          PATH_HOTELS_BOOK_CONFIRMATION,
          PATH_CARS_BOOK_CONFIRMATION,
          PATH_PACKAGES_BOOK_CONFIRMATION,
          PATH_ACTIVITIES_BOOK_CONFIRMATION,
          PATH_VACATION_RENTALS_BOOK_CONFIRMATION,
          PATH_PREMIER_COLLECTION_BOOK_CONFIRMATION,
        ]}
        render={(browserRouterProps: RouteComponentProps) =>
          renderGenericHeader(true, browserRouterProps, false)
        }
        exact
      />
      <Route path={PATH_FLIGHTS_SEARCH} />
      <Route path={[PATH_HOTELS_SEARCH, PATH_STAYS_SEARCH]} />
      <Route path={PATH_CARS_SEARCH} />
      <Route
        path={[
          PATH_PREMIER_COLLECTION_SEARCH,
          PATH_PREMIER_VACATION_RENTALS_SEARCH,
        ]}
      />

      <Route
        path="*"
        render={(browserRouterProps: RouteComponentProps) =>
          renderGenericHeader(
            false,
            browserRouterProps,
            isGlobalMobileNavExperiment &&
              HIDE_ON_SCROLL_PATHS.some((path) =>
                typeof path === "string"
                  ? path === browserRouterProps.location.pathname
                  : path.test(browserRouterProps.location.pathname)
              )
          )
        }
      />
    </Switch>
  );
};

export default MobileHeader;
